 import { useState, useEffect } from 'react'
import { Popup } from 'semantic-ui-react'
import { useProcessingJobs, useDashboardAggregates, useJobConflicts, useJobValidationRules, useJobIdentityResolution, useJobManualReviewErrors } from '../../../hooks/useProcessingJobs'
import { useRecentActivity } from '../../../hooks/useRecentActivity'
import { useAuthStore } from '../../../store/authStore'
import type { ProcessingJob } from '../../../types'
import './MonitorAndTrace.css'
import 'semantic-ui-css/semantic.min.css'

const MonitorAndTrace = () => {
  const { isReadOnlyMode } = useAuthStore()

  const [conflictsPage, setConflictsPage] = useState(1)
  const conflictsPerPage = 5

  const [jobsPage, setJobsPage] = useState(1)
  const jobsPerPage = 5 // Show 5 jobs per page

  const [selectedJob, setSelectedJob] = useState<ProcessingJob | null>(null)
  const [showDetailSections, setShowDetailSections] = useState(false)
  const [showJobDetails, setShowJobDetails] = useState(false)
  const [activeTab, setActiveTab] = useState<'jobDetails' | 'conflictResolution' | 'validation' | 'identityResolution' | 'errors'>('jobDetails')
  const [isRefetching, setIsRefetching] = useState(false)

  // Fetch processing jobs from API
  const {
    data: jobsResponse,
    isLoading: jobsLoading,
    error: jobsError,
    refetch: refetchJobs,
    isFetching: jobsRefetching
  } = useProcessingJobs({ page: jobsPage, per_page: jobsPerPage })

  // Fetch dashboard aggregates
  const {
    data: aggregatesResponse,
    isLoading: aggregatesLoading,
    error: aggregatesError,
    refetch: refetchAggregates
  } = useDashboardAggregates()

  // Fetch recent activity data
  const {
    data: recentActivityResponse,
    isLoading: recentActivityLoading,
    error: recentActivityError,
    refetch: refetchRecentActivity
  } = useRecentActivity({ time_window: '24h' })

  // Fetch conflicts for selected job
  const {
    data: conflictsResponse,
    isLoading: conflictsLoading,
    error: conflictsError,
    refetch: refetchConflicts
  } = useJobConflicts(
    selectedJob?.id || 0,
    { page: conflictsPage, per_page: conflictsPerPage },
    !!(selectedJob?.id && showDetailSections)
  )

  // Fetch validation rules for selected job
  const {
    data: validationRulesResponse,
    isLoading: validationRulesLoading,
    error: validationRulesError,
    refetch: refetchValidationRules
  } = useJobValidationRules(
    selectedJob?.id || 0,
    !!(selectedJob?.id && showDetailSections)
  )

  // Fetch identity resolution rules for selected job
  const {
    data: identityResolutionResponse,
    isLoading: identityResolutionLoading,
    error: identityResolutionError,
    refetch: refetchIdentityResolution
  } = useJobIdentityResolution(
    selectedJob?.id || 0,
    !!(selectedJob?.id && showDetailSections)
  )

  // Fetch manual review errors for selected job
  const {
    data: manualReviewErrorsResponse,
    isLoading: manualReviewErrorsLoading,
    error: manualReviewErrorsError,
    refetch: refetchManualReviewErrors
  } = useJobManualReviewErrors(
    selectedJob?.job_id || '',
    {},
    !!(selectedJob?.job_id && activeTab === 'errors')
  )

  // Refetch all data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setIsRefetching(true)
      try {
        await Promise.all([
          refetchJobs(),
          refetchAggregates(),
          refetchRecentActivity()
        ])
      } finally {
        setIsRefetching(false)
      }
    }
    fetchData()
  }, []) // Empty dependency array means this runs only on mount

  const jobs = jobsResponse?.data || []
  const totalJobs = jobsResponse?.total_count || 0
  const totalPages = totalJobs > 0 ? Math.ceil(totalJobs / jobsPerPage) : 1

  // Conflicts data from API
  const conflictsData = conflictsResponse?.data || []
  const totalConflicts = conflictsResponse?.pagination?.total_count || 0
  const conflictsTotalPages = totalConflicts > 0 ? Math.ceil(totalConflicts / conflictsPerPage) : 1

  // Validation rules data from API
  const validationRulesData = validationRulesResponse?.data || []

  // Identity resolution rules data from API
  const identityResolutionData = identityResolutionResponse?.data || []

  // Manual review errors data from API
  const manualReviewErrorsData = manualReviewErrorsResponse?.data || []
  const totalErrors = manualReviewErrorsResponse?.total_count || 0
  const pendingErrors = manualReviewErrorsResponse?.pending_count || 0
  const reviewedErrors = manualReviewErrorsResponse?.reviewed_count || 0

  const handleJobClick = (job: ProcessingJob) => {
    if (isReadOnlyMode) return // Disable job clicks in read-only mode

    setSelectedJob(job)
    setShowJobDetails(true)
    setActiveTab('jobDetails') // Always reset to first tab when clicking a job row
    setConflictsPage(1) // Reset conflicts page when switching jobs
    if (job.status === 'Completed' || job.status === 'Completed with errors' || job.status === 'Processed') {
      setShowDetailSections(true)
    } else {
      setShowDetailSections(false)
    }
  }

  const handleRefreshJobs = () => {
    refetchJobs()
    refetchAggregates()
    refetchRecentActivity()
    if (selectedJob && showDetailSections) {
      refetchConflicts()
      refetchValidationRules()
      refetchIdentityResolution()
    }
    if (selectedJob && activeTab === 'errors') {
      refetchManualReviewErrors()
    }
  }

  const handleBackToJobs = () => {
    setShowJobDetails(false)
    setSelectedJob(null)
    setShowDetailSections(false)
  }

  // Create metrics from API data or use defaults while loading/error
  const getMetricValue = (value: number | undefined, loading: boolean, error: any) => {
    if (loading) return '...'
    if (error) return 'N/A'
    return value?.toLocaleString() || '0'
  }

  const metrics = [
    {
      id: 1,
      title: 'Records Processed',
      value: getMetricValue(aggregatesResponse?.data?.total_records_processed, aggregatesLoading, aggregatesError),
      iconBg: '#DBEAFE',
      iconColor: '#155DFC',
      icon: 'document'
    },
    {
      id: 2,
      title: 'Conflicts Resolved',
      value: getMetricValue(aggregatesResponse?.data?.total_conflicts_resolved, aggregatesLoading, aggregatesError),
      iconBg: '#FEF3C6',
      iconColor: '#E17100',
      icon: 'alert'
    },
    {
      id: 3,
      title: 'Validation Rules',
      value: getMetricValue(aggregatesResponse?.data?.active_validation_rules, aggregatesLoading, aggregatesError),
      iconBg: '#D0FAE5',
      iconColor: '#009966',
      icon: 'check'
    },
    {
      id: 4,
      title: 'Identity Resolution Rules',
      value: getMetricValue(aggregatesResponse?.data?.active_identity_rules, aggregatesLoading, aggregatesError),
      iconBg: '#F3E8FF',
      iconColor: '#9810FA',
      icon: 'users'
    },
    {
      id: 5,
      title: 'Errors',
      value: getMetricValue(aggregatesResponse?.data?.total_records_failed, aggregatesLoading, aggregatesError),
      iconBg: '#FEC7C7',
      iconColor: '#DC2626',
      icon: 'shield'
    }
  ]

  // Helper function to format timestamps
  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'N/A'
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Helper function to get status class
  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'processed':
        return 'status-processed'
      case 'completed with errors':
        return 'status-completed-with-errors'
      case 'pending':
      case 'processing':
        return 'status-pending'
      case 'failed':
        return 'status-failed'
      default:
        return 'status-not-started'
    }
  }


  // Helper function to format processing time
  const formatProcessingTime = (seconds: number | undefined) => {
    if (seconds === undefined) return 'N/A'
    if (seconds < 60) return seconds?.toFixed(1)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return minutes > 0 ? `${minutes}m ${remainingSeconds.toFixed(0)}s` : seconds.toFixed(1)
  }

  // Helper function to get activity stat value
  const getActivityValue = (value: number | undefined, loading: boolean, error: any) => {
    if (loading) return '...'
    if (error) return 'N/A'
    return value?.toLocaleString() || '0'
  }

  // Helper function to format field names (snake_case to Title Case)
  const formatFieldName = (fieldName: string) => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  // Helper function to format file size to MB
  const formatFileSize = (fileSize: number | string | null | undefined) => {
    if (!fileSize) return '0 MB'
    
    // If it's already a formatted string (e.g., "1.5 MB"), return as is
    if (typeof fileSize === 'string') {
      const trimmed = fileSize.trim()
      // Check if it already has a unit (MB, KB, GB, bytes)
      if (/\s*(MB|KB|GB|bytes?)\s*$/i.test(trimmed)) {
        return trimmed
      }
      
      // Try to parse as number
      const parsed = parseFloat(trimmed)
      if (isNaN(parsed)) return fileSize // Return original if can't parse
      
      // If parsed number is small (< 1024), assume it's already in MB
      if (parsed < 1024) {
        return `${parsed.toFixed(2)} MB`
      }
      
      // Otherwise treat as bytes
      const megabytes = parsed / (1024 * 1024)
      if (megabytes < 1) {
        const kilobytes = parsed / 1024
        return `${kilobytes.toFixed(2)} KB`
      }
      return `${megabytes.toFixed(2)} MB`
    }
    
    // Handle numeric values
    const bytesNum = typeof fileSize === 'number' ? fileSize : 0
    if (bytesNum === 0) return '0 MB'
    
    // If number is small (< 1024), assume it's already in MB
    if (bytesNum < 1024) {
      return `${bytesNum.toFixed(2)} MB`
    }
    
    // Convert from bytes to appropriate unit
    const megabytes = bytesNum / (1024 * 1024)
    if (megabytes < 1) {
      const kilobytes = bytesNum / 1024
      return `${kilobytes.toFixed(2)} KB`
    }
    
    return `${megabytes.toFixed(2)} MB`
  }





  // Show loading state when refetching or initial load
  const showLoading = isRefetching || (jobsLoading && !jobsResponse) || (aggregatesLoading && !aggregatesResponse)

  return (
    <div className="monitor-container" style={{ position: 'relative', minHeight: '400px' }}>
      {/* Loading Overlay */}
      {showLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          minHeight: '400px'
        }}>
          <div className="loader-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div className="spinner" style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f4f6',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}></div>
            <p style={{ color: '#6b7280', fontSize: '14px', fontWeight: 500 }}>Loading monitor data...</p>
          </div>
        </div>
      )}
      
      {/* Read-Only Mode Banner */}
      {isReadOnlyMode && (
        <div className="readonly-mode-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <path d="M9 12l2 2 4-4"/>
          </svg>
          <span className="readonly-mode-text">
            Read-Only Mode: Job details and interactions are disabled for compliance user access.
          </span>
        </div>
      )}

      {/* Job List View */}
      {!showJobDetails && (
        <>
        

      {/* Metrics Grid */}
      <div className="monitor-metrics-grid">
        {metrics.map((metric) => (
          <div key={metric.id} className="monitor-metric-card">
            {/* Note: backgroundColor is dynamic based on metric data, so inline style is necessary */}
            <div className="metric-icon-wrapper" style={{ backgroundColor: metric.iconBg }}>
              {metric.id === 1 && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={metric.iconColor} strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              )}
              {metric.id === 2 && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={metric.iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              )}
              {metric.id === 3 && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={metric.iconColor} strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              {metric.id === 4 && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={metric.iconColor} strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              )}
              {metric.id === 5 && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={metric.iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M12 8v4"/>
                  <path d="M12 16h.01"/>
                </svg>
              )}
            </div>
            <div className="metric-content">
              <div className="metric-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{metric.title}</span>
                <Popup
                  trigger={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6B7280"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ cursor: 'pointer', flexShrink: 0 }}
                    >
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                  }
                  content={
                    metric.id === 1 ? 'Total number of records processed by the different jobs for all the files' :
                    metric.id === 2 ? 'Total number of conflicts resolved during file processing' :
                    metric.id === 3 ? 'Total number of fields for which the validation rules were applied during file processing' :
                    metric.id === 4 ? 'Total number of identities that were resolved during the file processing' :
                    metric.id === 5 ? 'Total number of errors found during file processing' : ''
                  }
                  position={metric.id >= 4 ? "top right" : "top center"}
                  style={{ maxWidth: '300px', textAlign: 'center', backgroundColor: '#FFFFFF', color: '#1F2937' }}
                />
              </div>
              <div className="metric-value">{metric.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Job Status Section */}
      <div className="job-status-section">
        <div className="job-status-header">
          <div className="job-status-title-group">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3366FF" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            <h2 className="job-status-title">Job Status</h2>
          </div>
          <button
            className="refresh-button"
            onClick={handleRefreshJobs}
            disabled={jobsRefetching || isReadOnlyMode}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={jobsRefetching ? 'animate-spin' : ''}
            >
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            {jobsRefetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="job-status-table-container">
          <table className="job-status-table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Data Source</th>
                <th>File Name</th>
                <th>Processing Method</th>
                <th>Total Records</th>
                <th>Status</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {jobsLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading jobs...
                    </div>
                  </td>
                </tr>
              ) : jobsError ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-red-600">
                    Error loading jobs: {jobsError.message}
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    No jobs found
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr
                    key={job.job_id}
                    onClick={() => handleJobClick(job)}
                    className={isReadOnlyMode ? 'job-row-disabled' : 'job-row-enabled'}
                  >
                    <td className="job-id">
                      <div className="job-id-container">
                        <span>{job.job_id}</span>
                        { job.status !== 'Processed' && (
                          <svg
                            className="job-error-icon"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ 
                              stroke: '#DC2626',
                              minWidth: '16px',
                              minHeight: '16px'
                            }}
                          >
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="source-badge-group">
                        <span className="source-badge">DS-{job.data_source_id}</span>
                        <span className="source-name">{job.data_source_name}</span>
                      </div>
                    </td>
                    <td className="file-name">{job.file_name}</td>
                    <td>
                      <div className="processing-method-badge">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          {job.processing_method === 'Manual Upload' ? (
                            <>
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="17 8 12 3 7 8"/>
                              <line x1="12" y1="3" x2="12" y2="15"/>
                            </>
                          ) : (
                            <>
                              <ellipse cx="12" cy="5" rx="9" ry="3"/>
                              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                            </>
                          )}
                        </svg>
                        <span>{job.processing_method}</span>
                      </div>
                    </td>
                    <td className="total-records">
                      {job.total_records ? job.total_records.toLocaleString() : 'N/A'}
                    </td>
                    <td>
                      <span className={`job-status-badge ${getStatusClass(job.status)}`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="timestamp">{formatTimestamp(job.completed_at || job.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="job-status-footer">
          <div className="job-status-info">
            {jobs.length > 0 && totalJobs > 0 ? (
              `Showing ${((jobsPage - 1) * jobsPerPage) + 1} to ${Math.min(jobsPage * jobsPerPage, totalJobs)} of ${totalJobs} jobs`
            ) : jobs.length > 0 ? (
              `Showing ${jobs.length} jobs`
            ) : (
              'No jobs to display'
            )}
          </div>

          {jobs.length > 0 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setJobsPage(prev => Math.max(prev - 1, 1))}
                disabled={jobsPage === 1 || jobsLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                Previous
              </button>

              <div className="pagination-pages">
                {totalPages > 1 && (
                  <>
                    {jobsPage > 2 && (
                      <>
                        <button className="pagination-page" onClick={() => setJobsPage(1)} disabled={jobsLoading}>1</button>
                        {jobsPage > 3 && <span className="pagination-ellipsis">...</span>}
                      </>
                    )}

                    {jobsPage > 1 && (
                      <button className="pagination-page" onClick={() => setJobsPage(jobsPage - 1)} disabled={jobsLoading}>
                        {jobsPage - 1}
                      </button>
                    )}

                    <button className="pagination-page pagination-page-active">
                      {jobsPage}
                    </button>

                    {jobsPage < totalPages && (
                      <button className="pagination-page" onClick={() => setJobsPage(jobsPage + 1)} disabled={jobsLoading}>
                        {jobsPage + 1}
                      </button>
                    )}

                    {jobsPage < totalPages - 1 && (
                      <>
                        {jobsPage < totalPages - 2 && <span className="pagination-ellipsis">...</span>}
                        <button className="pagination-page" onClick={() => setJobsPage(totalPages)} disabled={jobsLoading}>
                          {totalPages}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>

              <button
                className="pagination-btn"
                onClick={() => setJobsPage(prev => Math.min(prev + 1, totalPages))}
                disabled={jobsPage === totalPages || jobsLoading}
              >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>


          {/* Recent Upload Activity */}
          <div className="recent-upload-activity-section">
            <div className="activity-header">
              <h3 className="activity-section-title">Recent Activity</h3>
              <div className="activity-timeframe">Showing data for last 24 hours</div>
            </div>

            <div className="activity-stats-grid">
              <div className="activity-stat">
                <div className="stat-label">Manual Uploads</div>
                <div className="stat-value">
                  {getActivityValue(recentActivityResponse?.data?.manual_uploads, recentActivityLoading, recentActivityError)}
                </div>
                <div className="stat-unit">files</div>
              </div>

              <div className="activity-stat">
                <div className="stat-label">SFTP Uploads</div>
                <div className="stat-value">0</div>
                <div className="stat-unit">files</div>
              </div>

              <div className="activity-stat">
                <div className="stat-label">Average Processing Time</div>
                <div className="stat-value">
                  {recentActivityLoading ? '...' :
                   recentActivityError ? 'N/A' :
                   formatProcessingTime(recentActivityResponse?.data?.average_processing_time_seconds)}
                </div>
                <div className="stat-unit">seconds per file</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Job Details View */}
      {showJobDetails && selectedJob && (
        <div className="job-details-container">
          {/* Tabbed Navigation */}
          <div className="navigation-wrapper">
            <button
              onClick={handleBackToJobs}
              className="breadcrumb-back"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5" />
                <path d="M12 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Tab Navigation Bar */}
            <div className={`workflow-tabs-container ${showDetailSections ? 'workflow-tabs-container-full' : 'workflow-tabs-container-fit'}`}>
              <div className={`workflow-tabs ${showDetailSections ? 'workflow-tabs-full' : 'workflow-tabs-fit'}`}>
                <button
                  className={`workflow-tab ${activeTab === 'jobDetails' ? 'active' : ''}`}
                  onClick={() => setActiveTab('jobDetails')}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                  <span>Job Details</span>
                </button>

                {/* Only show these tabs for processed jobs */}
                {showDetailSections && (
                  <>
                    <button
                      className={`workflow-tab ${activeTab === 'conflictResolution' ? 'active' : ''}`}
                      onClick={() => setActiveTab('conflictResolution')}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <span>Conflict Resolution Strategies Agent</span>
                    </button>

                    <button
                      className={`workflow-tab ${activeTab === 'validation' ? 'active' : ''}`}
                      onClick={() => setActiveTab('validation')}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9 12l2 2 4-4" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                      <span>Validation & Normalization Rules Agent</span>
                    </button>

                    <button
                      className={`workflow-tab ${activeTab === 'identityResolution' ? 'active' : ''}`}
                      onClick={() => setActiveTab('identityResolution')}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      <span>Identity Resolution Rules Agent</span>
                    </button>

                    <button
                      className={`workflow-tab ${activeTab === 'errors' ? 'active' : ''}`}
                      onClick={() => setActiveTab('errors')}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <span>Errors</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Job Details Tab Content */}
          {activeTab === 'jobDetails' && (
            <>
          {/* Job Header */}
         

          {/* Job Information Cards */}
          <div className="job-info-grid-container job-info-grid-container-responsive">
          <div className="job-details-header">
            <div className="job-details-header-content">
              <svg className="job-details-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3366FF" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              <div className="job-details-text">
                <h1 className="job-details-title">Job Details - {selectedJob.job_id}</h1>
                <p className="job-details-subtitle">View and manage processing job details</p>
              </div>
            </div>
          </div>
            <div className="job-info-card-grid">
              {/* Row 1 */}
              <div className="job-info-card">
                <div className="job-info-label">Job ID</div>
                <div className="job-info-value">{selectedJob.job_id}</div>
              </div>

              <div className="job-info-card">
                <div className="job-info-label">Data Source</div>
                <div className="job-info-value">
                  <div className="data-source-info">
                    <span className="source-badge">DS-{selectedJob.data_source_id}</span>
                    <span className="source-name">{selectedJob.data_source_name}</span>
                  </div>
                </div>
              </div>

              <div className="job-info-card">
                <div className="job-info-label">Status</div>
                <div className="job-info-value">
                  <span className={`status-badge ${getStatusClass(selectedJob.status)}`}>
                    {selectedJob.status}
                  </span>
                </div>
              </div>

              {/* Row 2 */}
              <div className="job-info-card">
                <div className="job-info-label">File Name</div>
                <div className="job-info-value">{selectedJob.file_name}</div>
              </div>

              <div className="job-info-card">
                <div className="job-info-label">Processing Method</div>
                <div className="job-info-value">
                  <div className="processing-method-info">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {selectedJob.processing_method === 'Manual Upload' ? (
                        <>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </>
                      ) : (
                        <>
                          <ellipse cx="12" cy="5" rx="9" ry="3"/>
                          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                        </>
                      )}
                    </svg>
                    <span>{selectedJob.processing_method}</span>
                  </div>
                </div>
              </div>

              <div className="job-info-card">
                <div className="job-info-label">Timestamp</div>
                <div className="job-info-value">{formatTimestamp(selectedJob.completed_at || selectedJob.created_at)}</div>
              </div>

              {/* Row 3 */}
              <div className="job-info-card">
                <div className="job-info-label">Uploaded By</div>
                <div className="job-info-value">
                  {selectedJob.processing_method === 'SFTP' ? 'Automated SFTP' : 'Manual Upload User'}
                </div>
              </div>

              <div className="job-info-card">
                <div className="job-info-label">File Size</div>
                <div className="job-info-value">
                  {(() => {
                    console.log('File size value:', selectedJob.file_size, 'Type:', typeof selectedJob.file_size)
                    return formatFileSize(selectedJob.file_size)
                  })()}
                </div>
              </div>

              <div className="job-info-card">
                <div className="job-info-label">Records</div>
                <div className="job-info-value">
                  {selectedJob.total_records
                    ? `${selectedJob.records_processed?.toLocaleString() || '0'} / ${selectedJob.total_records.toLocaleString()}`
                    : `${selectedJob.records_processed?.toLocaleString() || '0'} processed`
                  }
                  {(selectedJob.records_failed || 0) > 0 && (
                    <span className="text-red-600 block text-sm">
                      {selectedJob.records_failed?.toLocaleString() || '0'} failed
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation Buttons for Job Details */}
            <div className="section-navigation-buttons">
              <button
                className="back-button"
                onClick={handleBackToJobs}
              >
                Back to Monitor
              </button>
              {showDetailSections && (
                <button
                  className="next-button"
                  onClick={() => setActiveTab('conflictResolution')}
                >
                  Next
                </button>
              )}
            </div>
          </div>
          </>
          )}

          {/* Conflict Resolution Tab Content */}
          {activeTab === 'conflictResolution' && showDetailSections && (
            <>
              <div className="field-definitions-section">
              {/* Conflicts Resolved Details Section */}
              <div className="conflicts-details-section">
                <div className="conflicts-details-header">
                  <div className="conflicts-details-title-group">
                    <svg className="conflict-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E17100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <h2 className="conflicts-details-title">Conflict Resolution Strategies Agent</h2>
                    <Popup
                      trigger={
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#6B7280" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          className="popup-trigger"
                        >
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="16" x2="12" y2="12"/>
                          <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                      }
                      content="Conflicts resolution agent parses the strategies defined under each rule and resolves the conflict between old/new data in our datastore"
                      position="top center"
                      className="popup-content-style"
                    />
                    
                  </div>
                </div>

                <div className="conflicts-list">
                  {conflictsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading conflicts...
                    </div>
                  ) : conflictsError ? (
                    <div className="text-center py-8 text-red-600">
                      Error loading conflicts: {conflictsError.message}
                    </div>
                  ) : conflictsData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No conflicts found for this job
                    </div>
                  ) : (
                    conflictsData.map((conflictRecord) => (
                      <div key={conflictRecord.record_id} className="mb-6">
                        {/* Record Header */}
                        <div className="conflict-item-header mb-4">
                          <div className="conflict-field-info">
                            <span className="conflict-field-label">Record ID: {conflictRecord.record_id || 'Unknown'}</span>
                            <span className="conflict-record-id">Profile #{conflictRecord.matched_profile_id || 'Unknown'}</span>
                          </div>
                          <span className="conflict-timestamp">{formatTimestamp(conflictRecord.timestamp || null)}</span>
                        </div>

                        {/* Individual Conflicts */}
                        {(conflictRecord.conflicts || []).map((conflict, conflictIndex) => (
                          <div key={conflictIndex} className="conflict-item-card">
                            <div className="conflict-item-header">
                              <div className="conflict-field-info">
                                <span className="conflict-field-label">
                                  Field: {conflict.field_label || 'Unknown'} ({conflict.field || 'Unknown'})
                                </span>
                              </div>
                            </div>

                            <div className="conflict-item-content">
                              <div className="conflict-values-section">
                                <div className="conflict-section-label">Conflicting Values</div>
                                <div className="conflict-value-item">
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                      <span className="conflict-source">Source:</span>
                                      <span className="conflict-value">{conflict.source_value || 'N/A'}</span>
                                    </div>
                                    {conflict.source_date && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Date: {formatTimestamp(conflict.source_date)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="conflict-value-item">
                                  <div className="flex items-center gap-2">
                                    <span className="conflict-source">New:</span>
                                    <span className="conflict-value">{conflict.new_value || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="conflict-resolution-section">
                                <div className="resolution-info">
                                  <div className="conflict-section-label">Resolution</div>
                                  {/* <div className="resolution-method">
                                    {conflict.resolution_strategy ?
                                      conflict.resolution_strategy.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) :
                                      'No resolution strategy'
                                    }
                                  </div> */}
                                  {conflict.reasoning && (
                                    <div className="resolution-method">{conflict.reasoning}</div>
                                  )}
                                </div>
                                <div className="resolved-value-info">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                    <polyline points="22 4 12 14.01 9 11.01"/>
                                  </svg>
                                  <span className="resolved-label">Resolved:</span>
                                  <span className="resolved-value">{conflict.resolved_value || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                </div>

                <div className="conflicts-footer">
                  <div className="conflicts-info">
                    {conflictsResponse?.message ||
                     `Showing ${((conflictsPage - 1) * conflictsPerPage) + 1} to ${Math.min(conflictsPage * conflictsPerPage, totalConflicts)} of ${totalConflicts} conflicts resolved`
                    }
                  </div>

                  {totalConflicts > 0 && (
                    <div className="pagination-controls">
                      <button
                        className="pagination-btn"
                        onClick={() => setConflictsPage(prev => Math.max(prev - 1, 1))}
                        disabled={conflictsPage === 1 || conflictsLoading || isReadOnlyMode}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="15 18 9 12 15 6"/>
                        </svg>
                        Previous
                      </button>

                      <div className="pagination-pages">
                        {conflictsTotalPages > 1 && (
                          <>
                            {conflictsPage > 1 && (
                              <button
                                className="pagination-page"
                                onClick={() => setConflictsPage(conflictsPage - 1)}
                                disabled={conflictsLoading || isReadOnlyMode}
                              >
                                {conflictsPage - 1}
                              </button>
                            )}

                            <button className="pagination-page pagination-page-active">
                              {conflictsPage}
                            </button>

                            {conflictsPage < conflictsTotalPages && (
                              <button
                                className="pagination-page"
                                onClick={() => setConflictsPage(conflictsPage + 1)}
                                disabled={conflictsLoading || isReadOnlyMode}
                              >
                                {conflictsPage + 1}
                              </button>
                            )}
                          </>
                        )}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() => setConflictsPage(prev => Math.min(prev + 1, conflictsTotalPages))}
                        disabled={conflictsPage === conflictsTotalPages || conflictsLoading || isReadOnlyMode}
                      >
                        Next
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                {/* Navigation Buttons for Conflict Resolution */}
                <div className="section-navigation-buttons">
                  <button 
                    className="back-button"
                    onClick={() => setActiveTab('jobDetails')}
                  >
                    Back
                  </button>
                  <button 
                    className="next-button"
                    onClick={() => setActiveTab('validation')}
                  >
                    Next
                  </button>
                </div>
              </div>
              </div>
            </>
          )}

          {/* Validation Tab Content */}
          {activeTab === 'validation' && showDetailSections && (
            <>
              <div className="field-definitions-section">
               

              {/* Validation Rules Details Section */}
              <div className="validation-rules-section">
                <div className="validation-rules-header">
                  <div className="validation-rules-title-group">
                    <div className="validation-icon-container">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                    </div>
                    <h2 className="validation-rules-title">Validation & Normalization Rules Agent</h2>
                    <Popup
                      trigger={
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#6B7280" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          className="popup-trigger"
                        >
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="16" x2="12" y2="12"/>
                          <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                      }
                      content="Validation and Transformation AI Agent parses the rules defined in plain text and applies to the data"
                      position="top center"
                      className="popup-content-style"
                    />
                  </div>
                </div>

                <div className="validation-rules-table-container">
                  <table className="validation-rules-table">
                    <thead>
                      <tr>
                        <th>Rule Name</th>
                        <th>Canonical Field</th>
                        <th>Description</th>
                        <th>Applied Count</th>
                        <th>Success Rate</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationRulesLoading ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8">
                            <div className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading validation rules...
                            </div>
                          </td>
                        </tr>
                      ) : validationRulesError ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-red-600">
                            Error loading validation rules: {validationRulesError.message}
                          </td>
                        </tr>
                      ) : validationRulesData.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-gray-500">
                            No validation rules found for this job
                          </td>
                        </tr>
                      ) : (
                        validationRulesData.map((rule, index) => (
                          <tr key={`${rule.rule_name}-${index}`}>
                            <td className="rule-name">{rule.rule_name}</td>
                            <td className="rule-field">{rule.canonical_field}</td>
                            <td className="rule-description">{rule.description}</td>
                            <td className="rule-count">{rule.applied_count.toLocaleString()}</td>
                            <td className="success-rate">
                              {rule.success_rate.toFixed(1)}%
                              <div className="text-xs text-gray-500 mt-1">
                                {rule.success_count.toLocaleString()} / {rule.applied_count.toLocaleString()} successful
                              </div>
                            </td>
                            <td>
                              <span className={`status-badge ${rule.status.toLowerCase() === 'active' ? 'status-active' : 'status-inactive'}`}>
                                {rule.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Navigation Buttons for Validation */}
                <div className="section-navigation-buttons">
                  <button 
                    className="back-button"
                    onClick={() => setActiveTab('conflictResolution')}
                  >
                    Back
                  </button>
                  <button 
                    className="next-button"
                    onClick={() => setActiveTab('identityResolution')}
                  >
                    Next
                  </button>
                </div>
              </div>

              </div>
            </>
          )}

          {/* Identity Resolution Tab Content */}
          {activeTab === 'identityResolution' && showDetailSections && (
            <>
              <div className="field-definitions-section">
               

              {/* Identity Resolution Rules Details Section */}
              <div className="identity-rules-section">
                <div className="identity-rules-header">
                  <div className="identity-rules-title-group">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9333EA" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    <h2 className="identity-rules-title">Identity Resolution Rules Agent</h2>
                    <Popup
                      trigger={
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#6B7280" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          className="popup-trigger"
                        >
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="16" x2="12" y2="12"/>
                          <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                      }
                      content="Identity Resolution Rules agent applies defined rules to match and merge records, ensuring unique identities in the datastore."
                      position="top center"
                      className="popup-content-style"
                    />
                  </div>
                </div>

                <div className="identity-rules-table-container">
                  <table className="identity-rules-table">
                    <thead>
                      <tr>
                        <th>Rule Name</th>
                        <th>Description</th>
                        <th>Match Type</th>
                        <th>Confidence</th>
                        <th>Avg Confidence</th>
                        <th>Matches Found</th>
                      </tr>
                    </thead>
                    <tbody>
                      {identityResolutionLoading ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8">
                            <div className="flex items-center justify-center">
                              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading identity resolution rules...
                            </div>
                          </td>
                        </tr>
                      ) : identityResolutionError ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-red-600">
                            Error loading identity resolution rules: {identityResolutionError.message}
                          </td>
                        </tr>
                      ) : identityResolutionData.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-8 text-gray-500">
                            No identity resolution rules found for this job
                          </td>
                        </tr>
                      ) : (
                        identityResolutionData.map((rule, index) => (
                          <tr key={`${rule.rule_name || 'rule'}-${index}`}>
                            <td className="rule-name">{rule.rule_name || 'Unnamed Rule'}</td>
                            <td className="rule-description">{rule.description || 'No description available'}</td>
                            <td>
                              <span className={`match-type-badge ${
                                rule.match_type && ['AUTO_MERGE', 'SSN_MATCH', 'NAME_DOB_MATCH'].includes(rule.match_type) ? 'match-exact' : 'match-fuzzy'
                              }`}>
                                {rule.match_type ? rule.match_type.replace(/_/g, ' ') : 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className={`confidence-badge ${
                                rule.confidence_level === 'High' ? 'confidence-high' :
                                rule.confidence_level === 'Medium' ? 'confidence-medium' : 'confidence-low'
                              }`}>
                                {rule.confidence_level || 'Unknown'}
                              </span>
                            </td>
                            <td className="avg-confidence">
                              {rule.average_confidence ? (rule.average_confidence * 100).toFixed(1) : '0.0'}%
                            </td>
                            <td className="matches-count">
                              {rule.matches_found ? rule.matches_found.toLocaleString() : '0'}
                              {rule.decision_breakdown && Object.keys(rule.decision_breakdown).length > 1 && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {Object.entries(rule.decision_breakdown)
                                    .filter(([type, count]) => type && count !== undefined)
                                    .map(([type, count]) => `${type.replace(/_/g, ' ')}: ${count}`)
                                    .join(', ')}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Navigation Buttons for Identity Resolution */}
                <div className="section-navigation-buttons">
                  <button 
                    className="back-button"
                    onClick={() => setActiveTab('validation')}
                  >
                    Back
                  </button>
                  <button 
                    className="next-button"
                    onClick={() => setActiveTab('errors')}
                  >
                    Next
                  </button>
                </div>
              </div>

              </div>
            </>
          )}

          {/* Errors Tab Content */}
          {activeTab === 'errors' && (
            <>
              <div className="errors-section-container">
                <div className="errors-section-header-flex">
                  <div className="errors-section-title-flex">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      <path d="M12 8v4"/>
                      <path d="M12 16h.01"/>
                    </svg>
                    <h3 className="errors-section-title-text">Errors</h3>
                  </div>
                  <div className="errors-section-stats-flex">
                    <div className="errors-stat-total">
                      Total: {totalErrors}
                    </div>
                    <div className="errors-stat-pending">
                      Pending: {pendingErrors}
                    </div>
                    <div className="errors-stat-reviewed">
                      Reviewed: {reviewedErrors}
                    </div>
                  </div>
                </div>

                {/* Errors Table */}
                <div className="errors-table-wrapper">
                  {manualReviewErrorsLoading ? (
                    <div className="errors-loading-wrapper">
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading errors...
                      </div>
                    </div>
                  ) : manualReviewErrorsError ? (
                    <div className="errors-error-wrapper">
                      Error loading manual review errors: {manualReviewErrorsError.message}
                    </div>
                  ) : manualReviewErrorsData.length === 0 ? (
                    <div className="errors-empty-wrapper">
                      No manual review errors found for this job
                    </div>
                  ) : (
                    <table className="errors-table-inline">
                      <thead>
                        <tr className="errors-table-row-border">
                          <th className="errors-table-header-style errors-table-header-record-id">Record ID</th>
                          <th className="errors-table-header-style errors-table-header-review-type">Review Type</th>
                          <th className="errors-table-header-style errors-table-header-error-category">Error Category</th>
                          <th className="errors-table-header-style errors-table-header-error-message">Error Message</th>
                          <th className="errors-table-header-style errors-table-header-status">Status</th>
                          <th className="errors-table-header-style errors-table-header-reviewed-by">Reviewed By</th>
                          <th className="errors-table-header-style errors-table-header-created-at">Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manualReviewErrorsData.map((error) => (
                          <tr key={error.id} className="errors-table-row-border">
                            <td className="errors-table-cell-record-id-style">
                              {error.record_id}
                            </td>
                            <td className="errors-table-cell-normal">
                              {formatFieldName(error.review_type)}
                            </td>
                            <td className="errors-table-cell-normal">
                              {formatFieldName(error.error_category)}
                            </td>
                            <td className="errors-table-cell-error">
                              {error.error_message}
                            </td>
                            <td className="errors-table-cell-status">
                              <span className={error.reviewed ? 'errors-status-badge-reviewed' : 'errors-status-badge-pending'}>
                                {error.reviewed ? 'Reviewed' : 'Pending'}
                              </span>
                            </td>
                            <td className="errors-table-cell-muted">
                              {error.reviewed_by || 'N/A'}
                            </td>
                            <td className="errors-table-cell-muted">
                              {formatTimestamp(error.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Navigation Buttons for Errors */}
                <div className="section-navigation-buttons">
                  <button 
                    className="back-button"
                    onClick={() => setActiveTab('identityResolution')}
                  >
                    Back
                  </button>
                  <button 
                    className="close-button"
                    onClick={handleBackToJobs}
                  >
                    Close
                  </button>
                </div>
              </div>
            </>
          )}

        </div>
      )}
    </div>
  )
}

export default MonitorAndTrace
